import React, { useEffect, useRef, useState, useMemo } from 'react';
import { FaHome, FaFacebook, FaInstagram, FaTwitter, FaLinkedin, FaYoutube, FaPhoneAlt, FaEnvelope, FaArrowUp } from 'react-icons/fa';
import FooterImage from '../../assets/layout/footerbg.png';
import AppStore from '../../assets/layout/appstore.png';
import GooglePlay from '../../assets/layout/googleplay.png';

const Footer = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const footerRef = useRef(null);

  // Office locations with enhanced SEO data
  const offices = useMemo(() => [
    {
      title: "Head Office Address:",
      address: "Rajendra Villa,\nSavitri Gali, opp- NRL Petrol pump, Pratap Nagar, Ramkrishna Nagar, Patna, Bihar, India, 800027",
      type: "HeadOffice",
      city: "Patna",
      state: "Bihar",
      country: "India",
      postalCode: "800027"
    },
    {
      title: "Corporate Office Address:",
      address: "1st Floor, Front Building, RoadExpress Tower, Exit 12 NH8, near Hero Honda Chowk, Gurugram, Haryana 122004",
      type: "CorporateOffice",
      city: "Gurugram",
      state: "Haryana",
      country: "India",
      postalCode: "122004"
    },
    {
      title: "International Office Address:",
      address: "Suite no 254, 7163 Younge street, Toronto, Ontario L3T 2A9.",
      type: "InternationalOffice",
      city: "Toronto",
      state: "Ontario",
      country: "Canada",
      postalCode: "L3T 2A9"
    }
  ], []);

  // Navigation links with proper URLs (placeholder for now)
  const navigationLinks = useMemo(() => [
    { name: 'Company', href: '/company', ariaLabel: 'Learn more about RoadExpress company' },
    { name: 'Shippers', href: '/shippers', ariaLabel: 'Shipping and logistics solutions' },
    { name: 'Carriers', href: '/carriers', ariaLabel: 'Join our carrier network' },
    { name: 'Support', href: '/support', ariaLabel: 'Customer support and help' },
    { name: 'Career', href: '/career', ariaLabel: 'Career opportunities at RoadExpress' }
  ], []);

  // Social media links with enhanced animations
  const socialMediaLinks = useMemo(() => [
    {
      icon: FaFacebook,
      color: 'hover:text-blue-400 hover:bg-blue-50',
      href: 'https://facebook.com/roadexpress.in',
      label: 'Follow RoadExpress on Facebook',
      platform: 'Facebook'
    },
    {
      icon: FaInstagram,
      color: 'hover:text-pink-400 hover:bg-pink-50',
      href: 'https://instagram.com/roadexpress_official/',
      label: 'Follow RoadExpress on Instagram',
      platform: 'Instagram'
    },
    {
      icon: FaTwitter,
      color: 'hover:text-blue-300 hover:bg-blue-50',
      href: 'https://x.com/road_express?s=08',
      label: 'Follow RoadExpress on X',
      platform: 'X'
    },
    {
      icon: FaLinkedin,
      color: 'hover:text-blue-500 hover:bg-blue-50',
      href: 'https://linkedin.com/company/roadexpress',
      label: 'Connect with RoadExpress on LinkedIn',
      platform: 'LinkedIn'
    },
    {
      icon: FaYoutube,
      color: 'hover:text-red-400 hover:bg-red-50',
      href: 'https://youtube.com/@roadexpress4052',
      label: 'Subscribe to RoadExpress YouTube channel',
      platform: 'YouTube'
    }
  ], []);

  // Legal links
  const legalLinks = useMemo(() => [
    { name: 'Privacy Policy', href: '/privacy' },
    { name: 'Terms of Service', href: '/terms' },
    { name: 'Logistics Terms', href: '/glossary' }
  ], []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      {
        threshold: 0.1,
        rootMargin: '50px'
      }
    );

    const currentRef = footerRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    // Scroll to top button logic
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 500);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Handle background image loading
  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const handleImageError = () => {
    setImageError(true);
    console.error('Failed to load footer background image');
  };

  // Handle phone call
  const handlePhoneCall = () => {
    window.location.href = 'tel:+918447738498';
  };

  // Handle email
  const handleEmailClick = () => {
    window.location.href = 'mailto:connect@roadexpressfreight.com';
  };

  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <>
      <footer ref={footerRef} className="flex flex-col w-full font-['Inter',sans-serif] mt-20 relative">
        {/* Main Footer with background image and overlay */}
        <div
          className="relative py-16 px-4 sm:px-6 lg:px-8 text-white overflow-hidden"
          style={{
            backgroundImage: !imageError ? `url(${FooterImage})` : 'none',
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            backgroundAttachment: 'scroll',
            position: 'relative',
            backgroundColor: imageError ? '#F05A28' : 'transparent'
          }}
        >
          {/* Background image with loading */}
          {!imageError && (
            <img
              src={FooterImage}
              alt=""
              className="absolute inset-0 w-full h-full object-cover opacity-0"
              onLoad={handleImageLoad}
              onError={handleImageError}
              loading="lazy"
              aria-hidden="true"
            />
          )}

          {/* Enhanced overlay with modern gradient */}
          <div
            className="absolute inset-0"
            style={{
              background: "radial-gradient(260.37% 260.37% at 50% 53.92%, rgba(240, 84, 35, 0.95) 0%, rgba(255, 255, 255, 0.513) 100%)",
              zIndex: 1
            }}
            aria-hidden="true"
          />

          {/* Animated decorative elements */}
          <div className="absolute inset-0 opacity-10 pointer-events-none z-0">
            <div className="absolute top-10 right-10 w-32 h-32 border-2 border-white rounded-full animate-pulse"></div>
            <div className="absolute bottom-20 left-10 w-24 h-24 border-2 border-white rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
            <div className="absolute top-1/2 left-1/4 w-16 h-16 border-2 border-white rounded-full animate-pulse" style={{ animationDelay: '2s' }}></div>
          </div>

          {/* Content positioned above the overlay */}
          <div className="relative z-10 max-w-7xl mx-auto">
            {/* Animated decorative line */}
            <div
              className={`border-t-2 border-white mb-12 transform transition-all duration-1000 ${isVisible ? 'translate-x-0 opacity-100 scale-x-100' : 'translate-x-[-100px] opacity-0 scale-x-0'
                }`}
              style={{ transformOrigin: 'left' }}
              aria-hidden="true"
            />

            <div className="flex flex-col lg:flex-row justify-between gap-12">
              {/* Left side - Address Information */}
              <section className="space-y-10 max-w-2xl" aria-labelledby="contact-info-title">
                <h2 id="contact-info-title" className="sr-only">Contact Information</h2>

                {offices.map((office, index) => (
                  <address
                    key={index}
                    className={`flex items-start space-x-4 transform transition-all duration-700 not-italic group ${isVisible
                      ? 'translate-y-0 opacity-100'
                      : 'translate-y-8 opacity-0'
                      }`}
                    style={{ transitionDelay: `${index * 0.2}s` }}
                  >
                    <div className="bg-white/90 backdrop-blur-sm p-3 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:scale-110 hover:rotate-3 group-hover:bg-white group-hover:shadow-orange-500/30 hover:shadow-lg hover:border-orange-200 border border-transparent">
                      <FaHome className="text-[#F05A28] text-xl transition-all duration-300 group-hover:scale-110 group-hover:text-orange-600" aria-hidden="true" />
                    </div>
                    <div className="transition-all duration-300 group-hover:translate-x-2 hover:bg-white/10 p-2 rounded-lg -ml-2">
                      <h3 className="font-semibold text-lg mb-2 group-hover:text-orange-200 transition-colors duration-300">{office.title}</h3>
                      <p className="text-sm leading-relaxed whitespace-pre-line opacity-90 group-hover:opacity-100 transition-opacity duration-300">
                        {office.address}
                      </p>
                    </div>
                  </address>
                ))}
              </section>

              {/* Right side - Navigation and Contact */}
              <aside className="lg:min-w-[400px]" aria-labelledby="footer-navigation-title">
                <h2 id="footer-navigation-title" className="sr-only">Navigation and Social Media</h2>

                <div className="flex flex-col md:flex-row lg:flex-col xl:flex-row gap-8">
                  {/* Navigation Links */}
                  <nav
                    className={`space-y-6 transform transition-all duration-700 ${isVisible
                      ? 'translate-y-0 opacity-100'
                      : 'translate-y-8 opacity-0'
                      }`}
                    style={{ transitionDelay: '0.3s' }}
                    aria-labelledby="footer-nav-title"
                  >
                    <h3 id="footer-nav-title" className="sr-only">Main Navigation</h3>
                    {navigationLinks.map((item, index) => (
                      <a
                        key={item.name}
                        href={item.href}
                        className="block font-medium text-sm hover:text-orange-200 transition-all duration-300 transform hover:translate-x-3 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-white/50 focus:rounded px-2 py-1 rounded-lg hover:bg-white/10 relative overflow-hidden group/link"
                        style={{ transitionDelay: `${0.4 + index * 0.1}s` }}
                        aria-label={item.ariaLabel}
                      >
                        <span className="relative z-10">
                        {item.name}
                        </span>
                        <div className="absolute inset-0 bg-gradient-to-r from-orange-400 to-orange-600 transform scale-x-0 group-hover/link:scale-x-100 transition-transform duration-300 origin-left opacity-20 rounded-lg"></div>
                      </a>
                    ))}
                  </nav>

                  {/* Social Media Icons */}
                  <nav
                    className={`space-y-5 flex md:flex-col transform transition-all duration-700 ${isVisible
                      ? 'translate-y-0 opacity-100'
                      : 'translate-y-8 opacity-0'
                      }`}
                    style={{ transitionDelay: '0.5s' }}
                    aria-labelledby="social-media-title"
                  >
                    <h3 id="social-media-title" className="sr-only">Social Media</h3>
                    {socialMediaLinks.map(({ icon: Icon, color, href, label, platform }, index) => (
                      <a
                        key={platform}
                        href={href}
                        className={`text-2xl transition-all duration-300 transform hover:scale-125 hover:-translate-y-1 hover:rotate-12 focus:outline-none focus:ring-2 focus:ring-white/50 focus:rounded p-3 rounded-2xl hover:shadow-lg hover:shadow-white/20 ${color} border border-transparent hover:border-white/30`}
                        style={{ transitionDelay: `${0.6 + index * 0.1}s` }}
                        aria-label={label}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Icon />
                      </a>
                    ))}
                  </nav>
                </div>

                {/* Contact Information */}
                <section
                  className={`mt-10 space-y-6 transform transition-all duration-700 ${isVisible
                    ? 'translate-y-0 opacity-100'
                    : 'translate-y-8 opacity-0'
                    }`}
                  style={{ transitionDelay: '0.7s' }}
                  aria-labelledby="contact-methods-title"
                >
                  <h3 id="contact-methods-title" className="sr-only">Contact Methods</h3>

                  <div className="flex items-center group cursor-pointer">
                    <div className="bg-white rounded-full p-3 mr-4 shadow-lg group-hover:shadow-xl transition-all duration-300 transform group-hover:scale-110 group-hover:rotate-6 hover:shadow-orange-500/30">
                      <FaEnvelope className="text-[#F05A28] text-lg group-hover:animate-bounce" aria-hidden="true" />
                    </div>
                    <a
                      href="mailto:connect@roadexpressfreight.com"
                      className="text-sm hover:text-orange-200 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-white/50 focus:rounded"
                      onClick={handleEmailClick}
                      aria-label="Send email to RoadExpress support"
                    >
                      connect@roadexpressfreight.com
                    </a>
                  </div>

                  <div className="flex">
                    <button
                      onClick={handlePhoneCall}
                      className="flex items-center bg-white text-[#F05A28] rounded-full px-6 py-3 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 hover:-translate-y-1 group focus:outline-none focus:ring-4 focus:ring-white/50 hover:shadow-orange-500/30 hover:bg-orange-50 border-2 border-transparent hover:border-orange-200 relative overflow-hidden"
                      aria-label="Call RoadExpress support at 0844 773 8498"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-orange-400 to-orange-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left opacity-10"></div>
                      <FaPhoneAlt className="mr-3 group-hover:animate-pulse relative z-10" aria-hidden="true" />
                      <span className="font-medium text-sm relative z-10">0844 773 8498</span>
                    </button>
                  </div>
                </section>
              </aside>
            </div>
          </div>
        </div>

        {/* Bottom Copyright Bar with enhanced design */}
        <div className="bg-orange-50 py-4 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row justify-between items-center gap-6">
              <p
                className={`text-gray-700 text-sm transform transition-all duration-700 ${isVisible
                  ? 'translate-y-0 opacity-100'
                  : 'translate-y-4 opacity-0'
                  }`}
                style={{ transitionDelay: '0.8s' }}
              >
                © 2025 RoadExpress Freight. All rights reserved.
              </p>

              <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-24">
                {/* App Store Buttons - keeping your exact design */}
                <div
                  className={`flex items-center justify-center gap-4 transform transition-all duration-700 ${isVisible
                    ? 'translate-y-0 opacity-100'
                    : 'translate-y-4 opacity-0'
                    }`}
                  style={{ transitionDelay: '0.9s' }}
                >
                  <a
                    href="https://play.google.com/store/apps/details?id=com.roadexpress"
                    className="transform hover:scale-110 transition-all duration-300 hover:shadow-lg hover:shadow-green-500/30 rounded-lg overflow-hidden focus:outline-none focus:ring-2 focus:ring-[#F05A28]/50 hover:-translate-y-2 hover:rotate-2 border-2 border-transparent hover:border-green-200 group"
                    aria-label="Download RoadExpress app from Google Play Store"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      src={GooglePlay}
                      alt="Download RoadExpress app from Google Play Store"
                      className="h-12"
                      loading="lazy"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        console.error('Failed to load Google Play store image');
                      }}
                    />
                  </a>
                  <a
                    href="https://apps.apple.com/app/roadexpress"
                    className="transform hover:scale-110 transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/30 rounded-lg overflow-hidden focus:outline-none focus:ring-2 focus:ring-[#F05A28]/50 hover:-translate-y-2 hover:-rotate-2 border-2 border-transparent hover:border-blue-200 group"
                    aria-label="Download RoadExpress app from Apple App Store"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      src={AppStore}
                      alt="Download RoadExpress app from Apple App Store"
                      className="h-12"
                      loading="lazy"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        console.error('Failed to load App Store image');
                      }}
                    />
                  </a>
                </div>

                {/* Legal Links */}
                <nav
                  className={`flex flex-wrap justify-center gap-6 transform transition-all duration-700 ${isVisible
                    ? 'translate-y-0 opacity-100'
                    : 'translate-y-4 opacity-0'
                    }`}
                  style={{ transitionDelay: '1s' }}
                  aria-labelledby="legal-links-title"
                >
                  <h3 id="legal-links-title" className="sr-only">Legal Information</h3>
                  {legalLinks.map((link, index) => (
                    <a
                      key={link.name}
                      href={link.href}
                      className="text-gray-700 text-sm hover:text-[#F05A28] hover:underline transition-all duration-300 transform hover:scale-105 hover:-translate-y-1 focus:outline-none focus:ring-2 focus:ring-[#F05A28]/50 focus:rounded px-3 py-2 rounded-lg hover:bg-orange-50 hover:shadow-sm relative overflow-hidden group/legal"
                      style={{ transitionDelay: `${1.1 + index * 0.1}s` }}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <span className="relative z-10">
                      {link.name}
                      </span>
                      <div className="absolute inset-0 bg-gradient-to-r from-orange-400 to-orange-600 transform scale-x-0 group-hover/legal:scale-x-100 transition-transform duration-300 origin-left opacity-10 rounded-lg"></div>
                    </a>
                  ))}
                </nav>
              </div>
            </div>
          </div>
        </div>

        {/* Structured Data for SEO */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "RoadExpress Freight",
            "description": "Leading logistics and transportation company in India",
            "url": "https://roadexpressfreight.com",
            "logo": "https://roadexpressfreight.com/logo.png",
            "contactPoint": [
              {
                "@type": "ContactPoint",
                "telephone": "+91-844-773-8498",
                "contactType": "customer service",
                "email": "connect@roadexpressfreight.com",
                "availableLanguage": ["English", "Hindi"]
              }
            ],
            "address": offices.map(office => ({
              "@type": "PostalAddress",
              "streetAddress": office.address.split('\n')[0],
              "addressLocality": office.city,
              "addressRegion": office.state,
              "postalCode": office.postalCode,
              "addressCountry": office.country
            })),
            "sameAs": socialMediaLinks.map(social => social.href),
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Logistics Services",
              "itemListElement": [
                {
                  "@type": "Offer",
                  "itemOffered": {
                    "@type": "Service",
                    "name": "Freight Transportation",
                    "description": "Comprehensive freight and logistics solutions"
                  }
                }
              ]
            }
          })}
        </script>
      </footer>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 z-50 bg-gradient-to-r from-[#F05A28] to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white p-4 rounded-2xl shadow-xl hover:shadow-orange-500/50 transition-all duration-300 transform hover:scale-110 hover:-translate-y-2 group border-2 border-transparent hover:border-orange-200 backdrop-blur-sm hover:backdrop-blur-lg"
          aria-label="Scroll to top"
        >
          <FaArrowUp className="w-4 h-4 group-hover:animate-bounce font-weight-600 group-hover:scale-110" />
          <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 rounded-2xl transition-opacity duration-300"></div>
        </button>
      )}
    </>
  );
};

export default Footer;